import { AlertCircle, ShieldAlert } from 'lucide-react';
import {
  Badge,
  Card,
  Heading,
  METRIC_READ_CAPTIONS,
  MetricCard,
  PageHeader,
  Skeleton,
} from '@aaimplatform/ui';
import { useProviderRateLimits } from '../../hooks/useProviderRateLimits';
import type {
  ProviderRateLimitConfig,
  WindowUsage,
} from '../../api/services/provider-rate-limits';
import { formatCount, formatRatioAsPercent } from '../../utils/display-format';
import { ProviderMark } from './provider-mark';

/**
 * Display names for the provider keys the rate-limit service returns. The keys
 * are the limiter's map keys (upper case); the names match the integration
 * catalog where the provider appears there. An unmapped key renders as-is.
 */
const PROVIDER_LABELS: Record<string, string> = {
  ATTOM: 'ATTOM Data',
  EXPERIAN: 'Experian',
  PROCISION: 'Procision',
  ELLIPTIC: 'Elliptic',
  FRED: 'FRED',
};

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

/**
 * A window duration in words: 60000 -> "1 min", 86400000 -> "1 day". Unit
 * words, not letters: an uppercased "1m" read as one million beside a request
 * count.
 */
export function formatWindowDuration(ms: number): string {
  if (ms < 60_000) return `${ms / 1000} sec`;
  if (ms < 3_600_000) return `${ms / 60_000} min`;
  if (ms < 86_400_000) {
    const hours = ms / 3_600_000;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  const days = ms / 86_400_000;
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * The limiter's window key as words: 'per-minute' -> "Per minute". The keys
 * are service map keys, not display text.
 */
export function windowLabel(label: string): string {
  const words = label.replace(/[-_]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Quota usage bar. Rendered only for a window the service reported usage for;
 * a window with no report shows the absence in words instead.
 */
export function QuotaBar({ usage }: { usage: WindowUsage }) {
  // A window with no maximum has no share to state: the share prints "--"
  // rather than a measured 0% (.claude/rules/no-fabricated-values.md).
  const share = usage.max > 0 ? usage.used / usage.max : null;
  const ratio = share === null ? 0 : share * 100;
  const barColor = usage.exhausted
    ? 'bg-error'
    : usage.warning
      ? 'bg-warning'
      : 'bg-neutral-data';

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-mono text-secondary tabular-nums">
          {formatCount(usage.used)} / {formatCount(usage.max)}
        </span>
        <span className="text-xs font-mono text-tertiary tabular-nums">
          {formatRatioAsPercent(share)}
        </span>
      </div>
      <div className="h-2 bg-recessed rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-500`}
          style={{ width: `${Math.min(ratio, 100)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Provider rate limit card. Read-only: the write operations answer 501
 * RATE_LIMIT_WRITE_NOT_AVAILABLE (ruling 30, answer rate-limit-write-routes
 * (b)), so the card offers no edit control.
 */
function ProviderCard({ config }: { config: ProviderRateLimitConfig }) {
  const name = providerLabel(config.provider);

  // quotaStatus is optional in the contract. When the service omits it, no
  // usage was measured, and the card says so rather than drawing 0 / N.
  const quotaStatus = config.quotaStatus;

  return (
    <Card className="p-0">
      {/* Header */}
      <div className="px-6 py-4 flex items-center gap-3 border-b border-recessed">
        <div className="flex items-center gap-3 min-w-0">
          <ProviderMark name={name} />
          <div className="min-w-0">
            <Heading level={2} as={3} size="card">{name}</Heading>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge variant={config.enabled ? 'success' : 'neutral'}>
                {config.enabled ? 'Limiting on' : 'Limiting off'}
              </Badge>
              {quotaStatus?.anyExhausted && (
                <span className="flex items-center gap-1 text-xs font-sans text-error font-medium">
                  <ShieldAlert className="w-3 h-3" aria-hidden="true" />
                  Quota exhausted
                </span>
              )}
              {quotaStatus?.anyWarning && !quotaStatus.anyExhausted && (
                <span className="flex items-center gap-1 text-xs font-sans text-warning font-medium">
                  <AlertCircle className="w-3 h-3" aria-hidden="true" />
                  Approaching limit
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quota Usage */}
      <div className="px-6 py-4">
        {config.windows.map((window, index) => {
          const usage = quotaStatus?.windows.find(w => w.label === window.label);
          return (
            <div key={window.label} className={index > 0 ? 'mt-4' : ''}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-sans font-medium text-primary">
                  {windowLabel(window.label)}{' '}
                  <span className="font-normal text-secondary">
                    ({formatWindowDuration(window.windowMs)})
                  </span>
                </span>
                <span className="text-xs font-sans text-secondary">
                  Limit: {formatCount(window.maxRequests)}
                </span>
              </div>
              {usage ? (
                <QuotaBar usage={usage} />
              ) : (
                <p className="text-xs font-sans text-tertiary">Usage not reported</p>
              )}
            </div>
          );
        })}
      </div>

    </Card>
  );
}

/**
 * The summary counts, from the providers that reported usage. When none did,
 * the counts are withheld: a zero would claim a measurement nobody made.
 */
export function summarizeQuota(providers: ProviderRateLimitConfig[]): {
  reporting: number;
  approaching: number | null;
  exhausted: number | null;
} {
  const reporting = providers.filter(p => p.quotaStatus !== undefined && p.quotaStatus !== null);
  if (reporting.length === 0) {
    return { reporting: 0, approaching: null, exhausted: null };
  }
  return {
    reporting: reporting.length,
    approaching: reporting.filter(p => p.quotaStatus?.anyWarning && !p.quotaStatus?.anyExhausted).length,
    exhausted: reporting.filter(p => p.quotaStatus?.anyExhausted).length,
  };
}

/**
 * Provider Rate Limits Configuration - Platform Admin
 *
 * Shows the request limits the platform-admin-api rate-limit routes hold for
 * external data providers. Usage counters live in the market-data service's
 * limiter and are not reported by these routes, so usage renders as not
 * reported. The page is read-only: the reset route resets nothing and the
 * write routes change nothing, both answering 501
 * (provider-rate-limits.routes.ts).
 */
export function ProviderRateLimitsConfigPage() {
  const { data, isLoading, error } = useProviderRateLimits();

  if (error) {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-error flex-shrink-0" aria-hidden="true" />
        <div className="text-sm font-sans text-error">
          Failed to load provider rate limits: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <div className="mb-6">
          <Skeleton className="h-5 w-80" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  const providers = data?.providers ?? [];
  const summary = summarizeQuota(providers);
  const partial = summary.reporting > 0 && summary.reporting < providers.length
    ? `From ${summary.reporting} of ${providers.length} providers reporting usage`
    : undefined;

  return (
    <div>
      <PageHeader description="Request limits for external data providers. Usage counters are kept by the market-data service's limiter and are not reported on this page. The limits shown are held in platform-admin-api memory: they do not reach the market-data limiter and cannot be changed from this page." />

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <MetricCard label="Providers" value={String(providers.length)} />
        <MetricCard
          label="Approaching limit"
          value={summary.approaching === null ? '--' : String(summary.approaching)}
          caption={summary.approaching === null ? METRIC_READ_CAPTIONS.notReported : undefined}
          description={partial}
        />
        <MetricCard
          label="Exhausted"
          value={summary.exhausted === null ? '--' : String(summary.exhausted)}
          caption={summary.exhausted === null ? METRIC_READ_CAPTIONS.notReported : undefined}
          description={partial}
        />
      </div>

      {/* Provider Cards */}
      {providers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-sm font-sans text-tertiary">No provider rate limits configured</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {providers.map(config => (
            <ProviderCard key={config.provider} config={config} />
          ))}
        </div>
      )}
    </div>
  );
}
